import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faLayerGroup, faListCheck, faLock } from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import Footer from '../ui/Footer';
import { useAuth } from '../../context/AuthContext';
import imtLogo from '../../assets/images/IMT_logo.png';
import MarketPricesTab from './MarketPricesTab';
import BenchmarksTab from './BenchmarksTab';
import TasksTab from './TasksTab';
// The shared tab bar (.hms-tabs / .tab-button / .tab-icon), as Reporting Central uses it.
import '../ui/HMSTabs.css';
import './PaperPricing.css';

export const PAPER_PRICING_REFUSAL = 'Paper Pricing is for the hedging desk. '
    + 'Ask an administrator for the Hedging Trader tick-box on Role Assignment.';

const TABS = [
    { value: 'prices', label: 'Market Prices', icon: faChartLine },
    { value: 'benchmarks', label: 'Benchmarks & Instruments', icon: faLayerGroup },
    { value: 'tasks', label: 'Tasks', icon: faListCheck },
];

// The tab lives in the address (?tab=prices|benchmarks|tasks), so a link or a
// refresh lands on the same tab. No tab, or one this screen does not have,
// opens Market Prices.
const tabFrom = (search) => {
    const wanted = new URLSearchParams(search).get('tab');
    return TABS.some((tab) => tab.value === wanted) ? wanted : 'prices';
};

/**
 * Paper Pricing (ledger row 132) -- the hedging desk's module: the daily price
 * board, the benchmark and paper-instrument lists, and the fixed-versus-floating
 * tasks.
 *
 * Who may open it is the server's answer (`can_open_paper_pricing`, decided by
 * market_pricing.permissions: the Hedging Trader tick or a superuser), read as
 * given and never rebuilt from other flags. Anyone else gets a plain panel and
 * the screen asks the server for nothing -- the prices are licensed per person.
 * Every request behind it is refused by the server regardless.
 */
const PaperPricing = () => {
    const { user } = useAuth();
    const location = useLocation();
    const history = useHistory();
    const canOpen = user?.can_open_paper_pricing === true;
    const activeTab = tabFrom(location.search);

    const openTab = (value) => {
        if (value === activeTab) return;
        history.push({ pathname: location.pathname, search: `?tab=${value}` });
    };

    return (
        <Layout showLeftNavSidebar showRightSidebar={false}>
            <div className="pp-hub">
                <div className="pp-header">
                    <div className="pp-title-section">
                        <img src={imtLogo} alt="IMT" className="pp-header-logo-img" />
                        <div>
                            <h1 className="hms-screen-title">Paper Pricing</h1>
                            <p className="pp-subtitle">
                                Market prices, benchmarks and paper instruments for the hedging desk
                            </p>
                        </div>
                    </div>
                </div>

                {!canOpen ? (
                    <div className="pp-content">
                        <div className="pp-panel pp-refusal" role="alert">
                            <FontAwesomeIcon icon={faLock} className="pp-refusal-icon" />
                            <p className="pp-refusal-text">{PAPER_PRICING_REFUSAL}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="hms-tabs">
                            <div className="tabs-container" role="tablist" aria-label="Paper Pricing">
                                {TABS.map((tab) => (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        role="tab"
                                        aria-selected={activeTab === tab.value}
                                        className={`tab-button ${activeTab === tab.value ? 'active' : ''}`}
                                        onClick={() => openTab(tab.value)}
                                    >
                                        <div className="tab-icon">
                                            <FontAwesomeIcon icon={tab.icon} />
                                        </div>
                                        <span>{tab.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="pp-content">
                            {activeTab === 'prices' && <MarketPricesTab />}
                            {activeTab === 'benchmarks' && <BenchmarksTab />}
                            {activeTab === 'tasks' && <TasksTab />}
                        </div>
                    </>
                )}

                <Footer />
            </div>
        </Layout>
    );
};

export default PaperPricing;
